import {
  AggregateDataFetch,
  AggregateUnitData,
  FilterFuncMap,
  useSiteViewColors,
  useSiteViewContext,
} from 'features/overview'
import { useRolodexEntries } from 'features/rolodex'
import { getExtraLabelDisplay as getExtraLabelDisplayBase } from 'features/siteMap/mapInteraction/GetExtraLabelDisplay'
import { MapInteractionAPIs } from 'features/siteMap/SiteViewMap'
import { useCallback, useState } from 'react'
import { useGeckoTheme } from 'styles/GeckoTheme'
import { GeckoGeographicPin, RolodexEntryType } from 'types'

import { shouldDisplayTaggedUnit as shouldDisplayTaggedUnitBase } from './shouldDisplayTaggedUnit'

export function useGetSiteViewMapInteractionAPIs(
  aggregateDataFetch: AggregateDataFetch,
  dataFilters: FilterFuncMap,
  selectedYear: number
) {
  const [unitIDFlyingTo, setUnitIDFlyingTo] = useState<string | undefined>(
    undefined
  )

  // Create a memoized callback for checking pin display
  const shouldDisplayPin = useCallback<
    NonNullable<MapInteractionAPIs['shouldDisplayPin']>
  >(async (pin: GeckoGeographicPin) => {
    const potreePointCloud = pin.tags[RolodexEntryType.POTREE_POINT_CLOUD]
    const hardcodedLink = pin.data.link

    // Display pin if it has either a hardcoded link OR a potree point cloud tag
    return !!(hardcodedLink || potreePointCloud)
  }, [])

  // a version of aggregateDataFetch that is indexable by unitID, this will allow our api-functions
  // to do lookups at O(1) time
  const indexableAggregateData: undefined | Record<string, AggregateUnitData> =
    aggregateDataFetch.isMainLoading
      ? undefined
      : aggregateDataFetch.aggregateSiteData.reduce<
          Record<string, AggregateUnitData>
        >((acc, unitData) => {
          acc[unitData.unitSpecs.unitData.id] = unitData
          return acc
        }, {})

  const shouldDisplayTaggedUnit = useCallback<
    NonNullable<MapInteractionAPIs['shouldDisplayTaggedUnit']>
  >(
    (unitID: string) =>
      shouldDisplayTaggedUnitBase(unitID, {
        indexableAggregateData,
        dataFilters,
      }),
    [dataFilters, indexableAggregateData]
  )

  const colors = useSiteViewColors()
  const { theme } = useGeckoTheme()
  const { site } = useSiteViewContext()

  const { riskAssessmentConfigs } = useRolodexEntries(
    !site
      ? undefined
      : {
          type: RolodexEntryType.RISK_ASSESSMENT_CONFIG,
          tags: { site: site.id },
          limit: 1,
        }
  )

  const riskAssessmentConfig = riskAssessmentConfigs.at(0)

  const getExtraLabelDisplay = useCallback<
    NonNullable<MapInteractionAPIs['getExtraLabelDisplay']>
  >(
    (unitID) =>
      getExtraLabelDisplayBase(unitID, {
        indexableAggregateData,
        siteViewColors: colors,
        theme: theme.forecast,
        selectedYear,
        riskAssessmentConfig,
      }),
    [
      colors,
      indexableAggregateData,
      theme.forecast,
      selectedYear,
      riskAssessmentConfig,
    ]
  )

  return {
    shouldDisplayTaggedUnit,
    getExtraLabelDisplay,
    unitIDFlyingTo,
    setUnitIDFlyingTo,
    shouldDisplayPin,
  }
}
